<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { sessionHelper } from "@/utils";
import { SHARED } from "@/constants";
import { mapState } from "vuex";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { paymentMethods, lookupMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

const PURPLES = ["#7C4DFF", "#9C6AFF", "#5E2FE0", "#B388FF", "#651FFF", "#6200EA"];

export default {
  page: { title: i18n.t("label.listingX", [i18n.t("module.payment")]) },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: i18n.t("label.listingX", [i18n.t("module.payment")]),
      currentPage: 1,
      filter: "",
      perPage: SHARED.DEFAULT_PAGE_SIZE,
      fields: [
        { key: 'docNo', sortable: true, label: i18n.t('label.docNo') },
        { key: 'docDate', sortable: true, label: i18n.t('label.docDate') },
        { key: 'debtorCode', sortable: true, label: i18n.t('label.debtorCode') },
        { key: 'debtorName', sortable: false, label: i18n.t('label.debtorName') },
        { key: 'amount', sortable: true, label: i18n.t('label.amount') },
        { key: 'paymentMethod', sortable: true, label: i18n.t('label.paymentMethod') },
        { key: 'action', sortable: false, label: i18n.t('label.action') },
      ],
      searchCriteria: {},
      viewing: null,
      loadingDetail: false,
      selectedDebtor: null,
    };
  },
  computed: {
    ...mapState("payment", ["status", "details"]),
    ...mapState(LOOKUP_MODULE, ["debtorDdl"]),
    tableRows() { return this.$store.state.payment.list.data; },
    pagination() { return this.$store.state.payment.list.pagination; },

    scopedRows() {
      if (!this.selectedDebtor) return this.tableRows;
      return this.tableRows.filter((r) => r.debtorCode === this.selectedDebtor.account_no);
    },
    totalCollected() {
      return this.scopedRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    },
    paymentCount() {
      return this.scopedRows.length;
    },
    uniqueDebtors() {
      return new Set(this.scopedRows.map((r) => r.debtorCode).filter(Boolean)).size;
    },
    avgPayment() {
      return this.paymentCount > 0 ? this.totalCollected / this.paymentCount : 0;
    },
    byMethod() {
      const map = {};
      for (const r of this.scopedRows) {
        const m = r.paymentMethod || "Unknown";
        if (!map[m]) map[m] = 0;
        map[m] += Number(r.amount) || 0;
      }
      return Object.entries(map).map(([method, amount]) => ({ method, amount }));
    },
    methodChartOptions() {
      return {
        chart: { fontFamily: "inherit" },
        labels: this.byMethod.map((r) => r.method),
        colors: PURPLES,
        legend: { position: "bottom", fontSize: "12px" },
        plotOptions: { pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Collected", formatter: () => `RM ${this.fmtShort(this.totalCollected)}` } } } } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
      };
    },
    methodChartSeries() {
      return this.byMethod.map((r) => r.amount);
    },
    byMonth() {
      // Group payments by YYYY-MM for trend chart
      const map = {};
      for (const r of this.scopedRows) {
        const month = (r.docDate || "").substr(0, 7); // YYYY-MM
        if (!month) continue;
        if (!map[month]) map[month] = 0;
        map[month] += Number(r.amount) || 0;
      }
      return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([month, amount]) => ({ month, amount }));
    },
    monthChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        colors: ["#7C4DFF"],
        xaxis: { categories: this.byMonth.map((m) => m.month), labels: { style: { fontSize: "11px" } } },
        yaxis: { labels: { formatter: (v) => `RM ${(v / 1000).toFixed(0)}k` } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
        grid: { borderColor: "#f1f3f5", strokeDashArray: 4 },
      };
    },
    monthChartSeries() {
      return [{ name: "Collected", data: this.byMonth.map((m) => m.amount) }];
    },
  },
  async created() {
    this.searchCriteria = { pageNumber: SHARED.DEFAULT_PAGE_NUMBER, pageSize: 100, searchArray: [] };
    await Promise.all([
      this.getList(this.searchCriteria),
      this.lookupDebtor({}),
    ]);
  },
  methods: {
    ...paymentMethods,
    ...lookupMethods,
    getList(filter) {
      this.getAllPayments({ filter });
      this.searchCriteria = filter;
      sessionHelper.storeSearch("PAYMENT_SEARCH", filter);
    },
    pageNavigation(pageNumber) {
      this.searchCriteria.pageNumber = pageNumber;
      this.getList(this.searchCriteria);
    },
    searchListing() {
      this.searchCriteria.pageNumber = SHARED.DEFAULT_PAGE_NUMBER;
      this.searchCriteria.searchArray = [this.filter];
      this.getList(this.searchCriteria);
    },
    onDebtorPick(d) { this.selectedDebtor = d; },
    async openDetail(row) {
      this.viewing = row;
      this.loadingDetail = true;
      this.$bvModal.show("paymentDetailModal");
      try {
        await this.getPayment({ docNo: row.docNo });
      } finally {
        this.loadingDetail = false;
      }
    },
    fmtRm(v) {
      const n = Number(v) || 0;
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtShort(v) {
      const n = Number(v) || 0;
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
      return n.toFixed(0);
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <b-alert show variant="info" class="mb-3">
      <i class="mdi mdi-information-outline mr-1"></i>
      Payments are recorded in AutoCount. This page is read-only.
    </b-alert>

    <!-- Debtor picker -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <label class="d-block mb-1 small text-muted">{{ $t('label.debtorCode') }}</label>
            <multiselect
              :value="selectedDebtor"
              :options="debtorDdl"
              :custom-label="(o) => `${o.account_no} — ${o.company_name || ''}`"
              track-by="account_no"
              :show-labels="false"
              :allow-empty="true"
              :placeholder="'All debtors (clear to see overall)'"
              @input="onDebtorPick"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- KPI cards -->
    <div class="row">
      <div class="col-6 col-md-3">
        <div class="card kpi kpi-ok">
          <div class="card-body">
            <div class="kpi-label">Total Collected</div>
            <div class="kpi-value">RM {{ fmtRm(totalCollected) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card kpi">
          <div class="card-body">
            <div class="kpi-label">Payments</div>
            <div class="kpi-value">{{ paymentCount }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card kpi">
          <div class="card-body">
            <div class="kpi-label">Unique Debtors</div>
            <div class="kpi-value">{{ uniqueDebtors }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card kpi">
          <div class="card-body">
            <div class="kpi-label">Avg Payment</div>
            <div class="kpi-value">RM {{ fmtRm(avgPayment) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts -->
    <div class="row">
      <div class="col-12 col-lg-5">
        <div class="card h-100">
          <div class="card-body">
            <h6 class="card-title">By Payment Method</h6>
            <apexchart v-if="totalCollected > 0" type="donut" height="280" :options="methodChartOptions" :series="methodChartSeries" />
            <div v-else class="text-muted text-center py-5">No payments</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-7">
        <div class="card h-100">
          <div class="card-body">
            <h6 class="card-title">Monthly Trend</h6>
            <apexchart v-if="byMonth.length" type="bar" height="280" :options="monthChartOptions" :series="monthChartSeries" />
            <div v-else class="text-muted text-center py-5">No payments</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center">
                  {{ $t('label.search') }}
                  <b-form-input v-model="filter" type="search" class="form-control form-control-sm ml-2" @change="searchListing"></b-form-input>
                </label>
              </div>
            </div>
            <div class="table-responsive mb-0">
              <b-table :items="scopedRows" :fields="fields" responsive="sm" :per-page="perPage" small>
                <template #cell(amount)="data">{{ fmtRm(data.item.amount) }}</template>
                <template #cell(debtorCode)="data">
                  <b-badge variant="light" class="font-monospace">{{ data.item.debtorCode }}</b-badge>
                </template>
                <template #cell(action)="data">
                  <a href="javascript:void(0);" @click="openDetail(data.item)" class="text-info" v-b-tooltip.hover :title="$t('label.view')">
                    <i class="mdi mdi-eye-outline font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>
            <div class="row" v-if="!selectedDebtor">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="pagination.total" :per-page="perPage" @change="pageNavigation"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="paymentDetailModal" :title="viewing ? `Payment — ${viewing.docNo}` : ''" size="lg" hide-footer scrollable>
      <div v-if="loadingDetail" class="text-center py-4">
        <b-spinner small variant="primary"></b-spinner>
      </div>
      <div v-else-if="details && details.docNo">
        <dl class="row mb-3">
          <dt class="col-sm-3">{{ $t('label.docNo') }}</dt><dd class="col-sm-9">{{ details.docNo }}</dd>
          <dt class="col-sm-3">{{ $t('label.docDate') }}</dt><dd class="col-sm-9">{{ details.docDate }}</dd>
          <dt class="col-sm-3">{{ $t('label.debtorCode') }}</dt>
          <dd class="col-sm-9">
            <b-badge variant="light" class="font-monospace">{{ details.debtorCode }}</b-badge>
            <span v-if="details.debtorName" class="ml-2">{{ details.debtorName }}</span>
          </dd>
          <dt class="col-sm-3">{{ $t('label.amount') }}</dt><dd class="col-sm-9"><strong>{{ fmtRm(details.amount) }}</strong></dd>
          <dt class="col-sm-3">{{ $t('label.paymentMethod') }}</dt><dd class="col-sm-9">{{ details.paymentMethod || '—' }}</dd>
          <template v-if="details.chequeNo">
            <dt class="col-sm-3">Cheque No</dt><dd class="col-sm-9">{{ details.chequeNo }}</dd>
          </template>
          <dt class="col-sm-3">{{ $t('label.description') }}</dt><dd class="col-sm-9">{{ details.description || '—' }}</dd>
        </dl>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
.kpi { border: 1px solid #e9ecef; }
.kpi .card-body { padding: 16px; }
.kpi-label { font-size: 11px; color: #8993A4; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.kpi-value { font-size: 22px; font-weight: 700; color: #212529; }
.kpi-ok .kpi-value { color: #28a745; }
</style>
